import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { UserPlus, Building, User, Mail, Lock, Phone, Briefcase } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { authService } from '../services/authService';
import { useTranslation } from '../hooks/useTranslation';

interface RegisterForm {
  // Entreprise
  company_name: string;
  company_type: string;
  
  // Personnel
  nom: string;
  telephone: string;
  
  // Compte
  email: string;
  password: string;
  confirmPassword: string;
}

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const t = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');

  const onSubmit = async (data: RegisterForm) => {
    try {
      setLoading(true);

      // D'abord créer le tenant (endpoint public dédié à l'onboarding)
      const tenant = await authService.registerTenant({
        nom: data.company_name,
        type: data.company_type,
      });

      // Ensuite créer l'utilisateur admin (le rôle admin_entreprise est
      // attribué côté serveur, jamais depuis le body — voir AuthService.register)
      await authService.register({
        nom: data.nom,
        email: data.email,
        telephone: data.telephone,
        password: data.password,
        tenant_id: tenant.id,
      });

      toast.success(t.register.success);
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t.register.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background avec dégradé animé */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-500 ">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
        
        {/* Bulles flottantes */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        {/* Language Switcher */}
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>

        {/* Carte d'inscription avec Glassmorphism */}
        <div className="w-full max-w-2xl">
          {/* Logo et titre */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 shadow-2xl mb-6">
              <UserPlus className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
              {t.register.title}
            </h1>
            <p className="text-white/90 text-lg">
              {t.register.subtitle}
            </p>
          </div>

          {/* Indicateur d'étapes */}
          <div className="flex justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all ${
                  s === step
                    ? 'w-12 bg-white'
                    : s < step
                    ? 'w-8 bg-white/70'
                    : 'w-8 bg-white/30'
                }`}
              />
            ))}
          </div>

          {/* Carte glassmorphism */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* ÉTAPE 1 : Informations Entreprise */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Building className="w-6 h-6 text-white" />
                    <h2 className="text-xl font-semibold text-white">
                      {t.register.companyInfo}
                    </h2>
                  </div>

                  {/* Nom entreprise */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t.register.companyName} *
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                      <input
                        type="text"
                        className={`w-full pl-11 pr-4 py-3 bg-white/10 border ${
                          errors.company_name ? 'border-red-400' : 'border-white/30'
                        } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md`}
                        placeholder="Transport Express"
                        {...register('company_name', {
                          required: t.register.companyNameRequired,
                        })}
                      />
                    </div>
                    {errors.company_name && (
                      <p className="mt-2 text-sm text-red-200">⚠ {errors.company_name.message}</p>
                    )}
                  </div>

                  {/* Type de compte — l'auto-inscription publique ne crée que des
                      comptes Particulier (individus / flottes express). Les
                      typologies entreprise exigent un onboarding Super Admin. */}
                  <input type="hidden" value="particulier" {...register('company_type')} />
                  <div className="flex items-start gap-3 bg-white/10 border border-white/30 rounded-xl p-4 backdrop-blur-md">
                    <Briefcase className="w-5 h-5 text-white/70 shrink-0 mt-0.5" />
                    <p className="text-sm text-white/80">
                      {t.register.particulierOnlyNotice}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full py-3 px-4 bg-white text-teal-600 rounded-xl font-semibold hover:bg-white/90 transition-all shadow-lg"
                  >
                    Suivant
                  </button>
                </div>
              )}

              {/* ÉTAPE 2 : Informations Personnelles */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <User className="w-6 h-6 text-white" />
                    <h2 className="text-xl font-semibold text-white">
                      {t.register.personalInfo}
                    </h2>
                  </div>

                  {/* Nom complet */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t.register.fullName} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                      <input
                        type="text"
                        className={`w-full pl-11 pr-4 py-3 bg-white/10 border ${
                          errors.nom ? 'border-red-400' : 'border-white/30'
                        } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md`}
                        placeholder="Jean Dupont"
                        {...register('nom', {
                          required: t.register.nameRequired,
                        })}
                      />
                    </div>
                    {errors.nom && (
                      <p className="mt-2 text-sm text-red-200">⚠ {errors.nom.message}</p>
                    )}
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t.register.phone} *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                      <input
                        type="tel"
                        className={`w-full pl-11 pr-4 py-3 bg-white/10 border ${
                          errors.telephone ? 'border-red-400' : 'border-white/30'
                        } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md`}
                        placeholder="+237 600 00 00 00"
                        {...register('telephone', {
                          required: t.register.phoneRequired,
                        })}
                      />
                    </div>
                    {errors.telephone && (
                      <p className="mt-2 text-sm text-red-200">⚠ {errors.telephone.message}</p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 px-4 bg-white/20 text-white rounded-xl font-semibold hover:bg-white/30 transition-all"
                    >
                      Retour
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="flex-1 py-3 px-4 bg-white text-teal-600 rounded-xl font-semibold hover:bg-white/90 transition-all shadow-lg"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}

              {/* ÉTAPE 3 : Informations Compte */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="w-6 h-6 text-white" />
                    <h2 className="text-xl font-semibold text-white">
                      {t.register.accountInfo}
                    </h2>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t.register.email} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                      <input
                        type="email"
                        className={`w-full pl-11 pr-4 py-3 bg-white/10 border ${
                          errors.email ? 'border-red-400' : 'border-white/30'
                        } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md`}
                        placeholder="exemple@email.com"
                        {...register('email', {
                          required: t.register.emailRequired,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t.register.emailInvalid,
                          },
                        })}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-200">⚠ {errors.email.message}</p>
                    )}
                  </div>

                  {/* Mot de passe */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t.register.password} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                      <input
                        type="password"
                        className={`w-full pl-11 pr-4 py-3 bg-white/10 border ${
                          errors.password ? 'border-red-400' : 'border-white/30'
                        } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md`}
                        placeholder="••••••••"
                        {...register('password', {
                          required: t.register.passwordRequired,
                          minLength: {
                            value: 6,
                            message: t.register.passwordMin,
                          },
                        })}
                      />
                    </div>
                    {errors.password && (
                      <p className="mt-2 text-sm text-red-200">⚠ {errors.password.message}</p>
                    )}
                  </div>

                  {/* Confirmer mot de passe */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t.register.confirmPassword} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                      <input
                        type="password"
                        className={`w-full pl-11 pr-4 py-3 bg-white/10 border ${
                          errors.confirmPassword ? 'border-red-400' : 'border-white/30'
                        } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md`}
                        placeholder="••••••••"
                        {...register('confirmPassword', {
                          required: t.register.confirmPasswordRequired,
                          validate: (value) =>
                            value === password || t.register.passwordsNotMatch,
                        })}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-2 text-sm text-red-200">⚠ {errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex-1 py-3 px-4 bg-white/20 text-white rounded-xl font-semibold hover:bg-white/30 transition-all"
                    >
                      Retour
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3 px-4 bg-white text-teal-600 rounded-xl font-semibold hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Loading...
                        </span>
                      ) : (
                        t.register.submit
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>

            {/* Lien de connexion */}
            <p className="text-center text-white/80 mt-6 text-sm">
              {t.register.hasAccount}{' '}
              <a href="/login" className="text-white font-semibold hover:underline">
                {t.register.login}
              </a>
            </p>
          </div>

          {/* Footer */}
          <p className="text-center text-white/60 text-xs mt-6">
            © 2026 CEDO Trace - Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
};
